// Audit-trail data layer (spec 16 §5). TanStack Query over the GENERATED
// api-client — doctrine 3: apps/web talks to the API only through
// `@assessly/api-client`, never a hand-rolled fetch. Read-only: there is no
// mutation to write here, and there never will be — the chain is append-only at
// the grant level (migration 0001 revokes UPDATE/DELETE/TRUNCATE).

import { useQuery } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { shouldRetryRead } from "../../../../lib/query";
import { unwrap } from "../../../../lib/request";

type S = components["schemas"];

export type AuditEntry = S["AuditEntry"];
export type AuditPageIntegrity = S["AuditPageIntegrity"];
export type AuditChainVerification = S["AuditChainVerification"];
export type AuditEntriesQuery = NonNullable<
  paths["/v1/audit/entries"]["get"]["parameters"]["query"]
>;

export const auditKeys = {
  all: ["audit"] as const,
  entries: (query: AuditEntriesQuery, cursor: string | undefined) =>
    ["audit", "entries", query, cursor ?? null] as const,
  verify: () => ["audit", "verify"] as const,
};

/**
 * One page of the trail. `enabled` is passed false when the client already
 * knows the read is refused — a read that will be refused should not be issued.
 */
export function useAuditEntries(
  query: AuditEntriesQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: auditKeys.entries(query, cursor),
    enabled,
    retry: shouldRetryRead,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/audit/entries", {
        signal,
        params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not load the audit trail");
    },
  });
}

/**
 * The chain verdict for the WHOLE chain (default range). Separate from the list
 * on purpose: it is a different question with a different, honestly-scoped
 * answer, and a screen that folded the two together would let a page's
 * "these rows are fine" be read as "the chain is fine".
 */
export function useAuditChainVerification(enabled = true) {
  return useQuery({
    queryKey: auditKeys.verify(),
    enabled,
    retry: shouldRetryRead,
    // The verifier walks up to 5 000 rows and sha256s each one. That is bounded
    // but it is not free, and the verdict does not change minute to minute on an
    // append-only chain — so it is held for the session rather than re-run every
    // time the reader adjusts a filter or navigates back. The KEY carries no
    // filter for the same reason: this answer is about the CHAIN, not about the
    // page, and a key that varied with the filters would quietly re-verify on
    // every keystroke while implying the two were related.
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/audit/chain/verify", { signal });
      return unwrap(res, "Could not verify the audit chain");
    },
  });
}
